import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material';
import toast from 'react-hot-toast';
import studentsService from '../../services/students';
import SalesPersonSelect from '../SalesPersonSelect';
import { getCountries, getStates, getCities, normalizeCountryForForm } from '../../data/locationData';
import { getAvailableTimezones, getTimezoneAbbreviation, normalizeTimezoneForStorage, formatTimezoneDisplay } from '../../utils/timezoneUtils';
import { toCalendarYmd, calculateAgeFromCalendarYmd } from '../../utils/dateTimeUtils';
import { getAgeGroupSelectOptions, ageToAgeGroup } from '../../constants/ageGroups';
import { getApiErrorMessage } from '../../utils/apiErrorMessage';

const STUDENT_TYPES = [
  { value: 'normal', label: 'Normal' },
  { value: 'autistic', label: 'Autistic' },
  { value: 'special_needs', label: 'Special needs' },
];
const CLASS_TYPES = [
  { value: 'one_on_one', label: '1:1' },
  { value: 'group', label: 'Group' },
];
const LEARNING_LEVELS = ['beginner', 'intermediate', 'advanced'];

function emptyForm() {
  return {
    first_name: '',
    last_name: '',
    phone: '',
    date_of_birth: '',
    age: '',
    class_grade: '',
    age_group: '',
    student_type: 'normal',
    class_type_preference: 'group',
    learning_level: 'beginner',
    support_requirements: '',
    father_name: '',
    mother_name: '',
    country: '',
    state: '',
    city: '',
    address: '',
    postal_code: '',
    timezone: 'UTC',
    assigned_to: null,
    notes: '',
  };
}

/**
 * Sales hub student edit — profile + closer. Time prefs / batch assign stay as separate actions.
 */
export default function AdmissionStudentEditDialog({ open, studentId, onClose, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [student, setStudent] = useState(null);
  const [form, setForm] = useState(emptyForm());

  useEffect(() => {
    if (!open || !studentId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await studentsService.getStudent(studentId);
        const s = res.data || res;
        if (cancelled) return;
        setStudent(s);
        setForm({
          first_name: s.first_name || '',
          last_name: s.last_name || '',
          phone: s.phone || '',
          date_of_birth: toCalendarYmd(s.date_of_birth) || '',
          age: s.age || '',
          class_grade: s.class_grade || '',
          age_group: s.age_group || '',
          student_type: s.student_type || 'normal',
          class_type_preference: s.class_type_preference || 'group',
          learning_level: s.learning_level || 'beginner',
          support_requirements: s.support_requirements || '',
          father_name: s.father_name || '',
          mother_name: s.mother_name || '',
          country: normalizeCountryForForm(s.country) || '',
          state: s.state || '',
          city: s.city || '',
          address: s.address || '',
          postal_code: s.postal_code || '',
          timezone: getTimezoneAbbreviation(s.timezone) || s.timezone || 'UTC',
          assigned_to: s.assigned_to || null,
          notes: s.notes || '',
        });
      } catch (err) {
        console.error(err);
        toast.error(getApiErrorMessage(err, 'Failed to load student'));
        onClose?.();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, studentId, onClose]);

  const setField = (key, value) => setForm((prev) => {
    const next = { ...prev, [key]: value };
    if (key === 'date_of_birth') {
      const age = calculateAgeFromCalendarYmd(value);
      if (age != null) {
        next.age = String(age);
        const group = ageToAgeGroup(age);
        if (group) next.age_group = group;
      }
    }
    if (key === 'age') {
      const group = ageToAgeGroup(value);
      if (group) next.age_group = group;
    }
    return next;
  });

  const handleSave = async () => {
    if (!studentId) return;
    setSaving(true);
    try {
      const updateData = {
        ...form,
        assigned_to: form.assigned_to === '' ? null : form.assigned_to,
        date_of_birth: form.date_of_birth ? toCalendarYmd(form.date_of_birth) : null,
        timezone: normalizeTimezoneForStorage(form.timezone),
        age: form.age === '' ? null : form.age,
      };
      await studentsService.updateStudent(studentId, updateData);
      toast.success('Student updated');
      onSaved?.();
      onClose?.();
    } catch (err) {
      console.error(err);
      toast.error(getApiErrorMessage(err, 'Failed to save student'));
    } finally {
      setSaving(false);
    }
  };

  const countries = getCountries();
  const states = form.country ? getStates(form.country) : [];
  const cities = form.country && form.state ? getCities(form.country, form.state) : [];
  const tzOptions = getAvailableTimezones();

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Edit student
        {student ? ` — ${student.first_name || ''} ${student.last_name || ''}`.trim() : ''}
        {student?.display_id ? ` (${student.display_id})` : ''}
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ pt: 1 }}>
            <Typography variant="subtitle1" color="primary" gutterBottom>
              Personal
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="First name" value={form.first_name} onChange={(e) => setField('first_name', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Last name" value={form.last_name} onChange={(e) => setField('last_name', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Email" value={student?.email || ''} disabled helperText="Email cannot be changed" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Phone" value={form.phone} onChange={(e) => setField('phone', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Date of birth"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={form.date_of_birth || ''}
                  onChange={(e) => setField('date_of_birth', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField fullWidth size="small" label="Age" value={form.age} onChange={(e) => setField('age', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField fullWidth size="small" label="Class / grade" value={form.class_grade} onChange={(e) => setField('class_grade', e.target.value)} />
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" color="primary" gutterBottom>
              Academic
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Age group</InputLabel>
                  <Select label="Age group" value={form.age_group || ''} onChange={(e) => setField('age_group', e.target.value)}>
                    {getAgeGroupSelectOptions(form.age_group).map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Student type</InputLabel>
                  <Select label="Student type" value={form.student_type || 'normal'} onChange={(e) => setField('student_type', e.target.value)}>
                    {STUDENT_TYPES.map((t) => (
                      <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Class preference</InputLabel>
                  <Select label="Class preference" value={form.class_type_preference || 'group'} onChange={(e) => setField('class_type_preference', e.target.value)}>
                    {CLASS_TYPES.map((t) => (
                      <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Learning level</InputLabel>
                  <Select label="Learning level" value={form.learning_level || 'beginner'} onChange={(e) => setField('learning_level', e.target.value)}>
                    {LEARNING_LEVELS.map((l) => (
                      <MenuItem key={l} value={l}>{l}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Support requirements" value={form.support_requirements} onChange={(e) => setField('support_requirements', e.target.value)} multiline rows={2} />
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" color="primary" gutterBottom>
              Parents & location
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Father name" value={form.father_name} onChange={(e) => setField('father_name', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Mother name" value={form.mother_name} onChange={(e) => setField('mother_name', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Country</InputLabel>
                  <Select
                    label="Country"
                    value={form.country || ''}
                    onChange={(e) => setForm((prev) => ({ ...prev, country: e.target.value, state: '', city: '' }))}
                  >
                    {countries.map((c) => (
                      <MenuItem key={c} value={c}>{c}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Timezone</InputLabel>
                  <Select label="Timezone" value={form.timezone || 'UTC'} onChange={(e) => setField('timezone', e.target.value)}>
                    {tzOptions.map((tz) => (
                      <MenuItem key={tz.value} value={tz.value}>{tz.label}</MenuItem>
                    ))}
                    {form.timezone && !tzOptions.some((t) => t.value === form.timezone) && (
                      <MenuItem value={form.timezone}>{formatTimezoneDisplay(form.timezone)}</MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small" disabled={!form.country}>
                  <InputLabel>State</InputLabel>
                  <Select label="State" value={form.state || ''} onChange={(e) => setForm((prev) => ({ ...prev, state: e.target.value, city: '' }))}>
                    {states.map((s) => (
                      <MenuItem key={s} value={s}>{s}</MenuItem>
                    ))}
                    {form.state && !states.includes(form.state) && (
                      <MenuItem value={form.state}>{form.state}</MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small" disabled={!form.state}>
                  <InputLabel>City</InputLabel>
                  <Select label="City" value={form.city || ''} onChange={(e) => setField('city', e.target.value)}>
                    {cities.map((c) => (
                      <MenuItem key={c} value={c}>{c}</MenuItem>
                    ))}
                    {form.city && !cities.includes(form.city) && (
                      <MenuItem value={form.city}>{form.city}</MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Zip code" value={form.postal_code} onChange={(e) => setField('postal_code', e.target.value)} inputProps={{ maxLength: 20 }} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Street address" value={form.address} onChange={(e) => setField('address', e.target.value)} />
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" color="primary" gutterBottom>
              Sales & notes
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <SalesPersonSelect
                  value={form.assigned_to || ''}
                  onChange={(v) => setField('assigned_to', v)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Notes" value={form.notes} onChange={(e) => setField('notes', e.target.value)} multiline rows={3} />
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={loading || saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
